import { Task } from '../../../types/Task';
import { Effort } from '../../../types/Effort';
import { calculateLabourLineTotal } from '../../../utils/labourOvertimeDisplay';

/** Normalize effort.type for matching (forms write "Subcontractor", calcs historically expected "subcontractor"). */
const effortTypeKey = (type: string | null | undefined): string =>
  (type || '').toLowerCase().replace(/\s+/g, '');

const isEffortType = (effort: { type?: string | null }, ...types: string[]): boolean => {
  const key = effortTypeKey(effort?.type);
  return types.some((t) => effortTypeKey(t) === key);
};

/** Pre-markup per-type subtotals, the only effort-derived input the Cost Plus formula needs. */
type RevenueBase = {
  labour: number;
  material: number;
  subcontractor: number;
  vehicle: number;
  hiredEquipment: number;
  other: number;
};

const sumBy = (
  efforts: Effort[],
  amount: (effort: any) => number,
  ...types: string[]
): number =>
  efforts
    .filter((effort: any) => isEffortType(effort, ...types))
    .reduce((sum: number, effort: any) => sum + amount(effort), 0);

/**
 * Derives the per-type bases from full effort rows.
 *
 * `material` prices from `effort.rate`: Daily Logs store a material's unit price there, and Effort
 * has no unitCost column - reading one billed every material at 0. The server's summary prices the
 * same way, so the two agree.
 */
const revenueBaseFromEfforts = (efforts: Effort[]): RevenueBase => ({
  labour: sumBy(efforts, (e) => calculateLabourLineTotal(e), 'labour'),
  material: sumBy(efforts, (e) => (e.rate || 0) * (e.quantity || 0), 'material'),
  subcontractor: sumBy(efforts, (e) => (e.rate || 0) * (e.quantity || 0), 'subcontractor'),
  vehicle: sumBy(efforts, (e) => (e.rate || 0) * (e.quantity || 0), 'vehicle'),
  hiredEquipment: sumBy(
    efforts,
    (e) => (e.rate || 0) * (e.quantity || 0),
    'Hired Equipment',
    'hired equipment',
    'hiredequipment'
  ),
  other: sumBy(efforts, (e) => (e.rate || 0) * (e.quantity || 0), 'other'),
});

const ZERO_REVENUE_BASE: RevenueBase = {
  labour: 0,
  material: 0,
  subcontractor: 0,
  vehicle: 0,
  hiredEquipment: 0,
  other: 0,
};

/**
 * The effort-derived input for Cost Plus, from whichever source the task has.
 *
 * Full rows win when present (task modal, expanded row) so nothing changes there. List rows have no
 * rows and fall back to the server's summary, whose values are the same subtotals this would have
 * produced - the markups are applied to the per-type total, not per row, so the two agree exactly.
 */
const resolveRevenueBase = (task: Task): RevenueBase => {
  if (task.efforts) return revenueBaseFromEfforts(task.efforts);
  return task.effortSummary?.revenueBase ?? ZERO_REVENUE_BASE;
};

/** True when this task has any effort carrying a quantity or a rate, from either source. */
export const hasPriceableEfforts = (task: Task | undefined): boolean => {
  if (!task) return false;
  if (task.efforts) return task.efforts.some((e) => e.quantity || e.rate);
  return task.effortSummary?.hasPriceable ?? false;
};

/**
 * Latest effort date, from either source.
 *
 * The completion modal used to scan task.efforts for this. Extracted here so it works from the
 * server summary on list rows, which is how that modal is always opened.
 */
export const getLatestEffortDate = (task: Task | undefined): Date | null => {
  if (!task) return null;

  if (!task.efforts) {
    const summaryDate = task.effortSummary?.latestDate;
    if (!summaryDate) return null;
    const parsed = new Date(summaryDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const times = task.efforts
    .filter((effort) => effort.date)
    .map((effort) => new Date(effort.date!).getTime())
    .filter((time) => !Number.isNaN(time));

  return times.length === 0 ? null : new Date(Math.max(...times));
};

/** Effort row count, from either source. */
export const getEffortCount = (task: Task | undefined): number => {
  if (!task) return 0;
  if (task.efforts) return task.efforts.length;
  return task.effortSummary?.count ?? 0;
};

/**
 * Raw cost total: Other rows contribute quantity alone; labour uses DRM OT when persisted;
 * everything else quantity x rate. Markups apply to revenue, never to this figure.
 */
export const getEffortCostTotal = (task: Task | undefined): number => {
  if (!task) return 0;
  if (task.efforts) {
    return task.efforts.reduce((total, effort: any) => {
      const isOther = isEffortType(effort, 'other');
      const isLabour = isEffortType(effort, 'labour');
      const cost = isOther
        ? (effort.quantity ?? 0)
        : isLabour
          ? calculateLabourLineTotal(effort)
          : (effort.quantity ?? 0) * (effort.rate ?? 0);
      return total + cost;
    }, 0);
  }
  return task.effortSummary?.costTotal ?? 0;
};

/**
 * Get the earliest effort date from task efforts
 * This is used to calculate the actual start date of a task
 * 
 * @param task - The task with its efforts
 * @returns The earliest effort date or null if no valid dates found
 */
export const getEarliestEffortDate = (task: Task): Date | null => {
  // List rows carry no effort array - the server sends the earliest date in the summary instead.
  // Full rows win when present, so the task modal keeps computing this from real data.
  if (!task.efforts) {
    const summaryDate = task.effortSummary?.earliestDate;
    if (!summaryDate) return null;
    const parsed = new Date(summaryDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (task.efforts.length === 0) {
    return null;
  }

  // Filter out efforts without dates and convert to Date objects
  const effortDates = task.efforts
    .filter((effort: Effort) => effort.date)
    .map((effort: Effort) => {
      const date = new Date(effort.date!);
      return date;
    })
    .filter(date => !isNaN(date.getTime())); // Filter out invalid dates


  if (effortDates.length === 0) {
    return null;
  }

  // Return the earliest date
  const earliestDate = new Date(Math.min(...effortDates.map(date => date.getTime())));
  return earliestDate;
};

/**
 * Calculate the total value for a quantified task based on quantity and task costing
 * This is the centralized calculation logic used across all components
 * 
 * @param task - The task with its costings and efforts
 * @param quantity - The quantity to use for calculation (identifiedQuantity for expected, completionAccomplishment for actual)
 * @param options - Optional configuration for calculation
 * @returns The calculated total value
 */
export const calculateTotalValue = (
  task: Task | undefined, 
  quantity: number,
  options?: {
    enableDebug?: boolean;
    usePercentageMarkup?: boolean;
  }
): number => {
  if (!task) return 0;
  
  const taskCosting = task.taskCostings?.[0];
  if (!taskCosting) return 0;

  const enableDebug = options?.enableDebug || false;
  const usePercentageMarkup = options?.usePercentageMarkup ?? true; // Default to percentage markup

  if (enableDebug) {
  }

  if (taskCosting.taskType === 'Unit Rate') {
    const baseRate = taskCosting.baseRate || 0;
    const rateMultiplier = taskCosting.rateMultiplier || 1;
    const rateAdjustment = taskCosting.rateAdjustment || 0;
    
    // If there's a rate adjustment, use it instead of base rate
    // Otherwise use the base rate
    const effectiveRate = rateAdjustment > 0 ? rateAdjustment : baseRate;
    const adjustedRate = effectiveRate * rateMultiplier;
    const result = adjustedRate * quantity;
    
    if (enableDebug) {
    }
    return result;
  } else if (taskCosting.taskType === 'Lump Sum') {
    if (taskCosting.costType === 'Fixed Cost') {
      // For Lump Sum Fixed Cost, always use the Expected Value
      const result = taskCosting.totalValue || 0;
      
      if (enableDebug) {
      }
      return result;
    } else if (taskCosting.costType === 'Cost Plus') {
      // Cost Plus prices the work from the effort that went into it, per type, each type carrying
      // its own markup.
      //
      // This used to filter and reduce task.efforts inline. It now works from per-type subtotals,
      // which come from the effort rows when the caller has them (task modal, expanded row) and
      // from the server's effortSummary when it does not (list rows). The result is identical
      // either way: the markup multiplies the per-TYPE total, so summing rows then multiplying is
      // the same arithmetic as multiplying a pre-computed subtotal.
      const base = resolveRevenueBase(task);

      const markupOf = (markup: number | null | undefined): number =>
        usePercentageMarkup ? 1 + (markup || 0) / 100 : 1 + (markup || 0);

      // `other` is deliberately last and unmarked-up - it has never had a markup applied.
      const totalValue =
        base.labour * markupOf(taskCosting.labourMarkup) +
        base.material * markupOf(taskCosting.materialMarkup) +
        base.subcontractor * markupOf(taskCosting.subcontractorMarkup) +
        base.vehicle * markupOf(taskCosting.vehicleMarkup) +
        base.hiredEquipment * markupOf(taskCosting.hiredEquipmentMarkup) +
        base.other;

      if (enableDebug) {
      }
      return totalValue;
    }
  }
  
  const fallbackValue = taskCosting.totalValue || 0;
  if (enableDebug) {
  }
  return fallbackValue;
};

/**
 * Calculate the actual total value for a quantified task based on completion accomplishment
 * This is the centralized calculation logic used across all components
 * 
 * @param task - The task with its costings and efforts
 * @param completionAccomplishment - The actual accomplishment value
 * @param options - Optional configuration for calculation
 * @returns The calculated actual total value
 */
export const calculateActualTotalValue = (
  task: Task | undefined, 
  completionAccomplishment: number,
  options?: {
    enableDebug?: boolean;
    usePercentageMarkup?: boolean;
  }
): number => {
  return calculateTotalValue(task, completionAccomplishment, options);
};

/**
 * Calculate the expected total value for a task using the same logic as actual value calculation
 * but with the identified quantity instead of completion accomplishment
 * @param task - The task with its costings
 * @param options - Optional configuration for calculation
 * @returns The expected total value
 */
export const calculateExpectedTotalValueWithLogic = (
  task: Task | undefined,
  options?: {
    enableDebug?: boolean;
    usePercentageMarkup?: boolean;
  }
): number => {
  // Get the identified quantity from the quantified task
  const identifiedQuantity = task?.quantifiedTasks?.[0]?.identifiedQuantity || 0;
  return calculateTotalValue(task, identifiedQuantity, options);
};

/**
 * Calculate the expected total value for a task
 * @param task - The task with its costings
 * @returns The expected total value
 */
export const calculateExpectedTotalValue = (task: Task | undefined): number => {
  if (!task) return 0;
  
  const taskCosting = task.taskCostings?.[0];
  if (!taskCosting) return 0;

  return taskCosting.totalValue || 0;
};

/**
 * Calculate the variance between expected and actual values
 * @param expectedValue - The expected total value
 * @param actualValue - The actual total value
 * @returns The variance (actual - expected)
 */
export const calculateValueVariance = (expectedValue: number, actualValue: number): number => {
  return actualValue - expectedValue;
};

/**
 * Calculate the variance percentage
 * @param expectedValue - The expected total value
 * @param actualValue - The actual total value
 * @returns The variance percentage
 */
export const calculateValueVariancePercentage = (expectedValue: number, actualValue: number): number => {
  if (expectedValue === 0) return 0;
  return ((actualValue - expectedValue) / expectedValue) * 100;
};

/**
 * Get the calculation breakdown for debugging purposes
 * @param task - The task with its costings and efforts
 * @param completionAccomplishment - The actual accomplishment value
 * @returns Detailed breakdown of the calculation
 */
export const getCalculationBreakdown = (
  task: Task | undefined, 
  completionAccomplishment: number
): {
  taskType: string;
  costType: string;
  baseRate: number;
  rateMultiplier: number;
  completionAccomplishment: number;
  expectedValue: number;
  actualValue: number;
  variance: number;
  variancePercentage: number;
  effortBreakdown?: {
    labour: { count: number; value: number; markup: number; total: number };
    material: { count: number; value: number; markup: number; total: number };
    subcontractor: { count: number; value: number; markup: number; total: number };
    vehicle: { count: number; value: number; markup: number; total: number };
    other: { count: number; value: number; markup: number; total: number };
  };
} => {
  const taskCosting = task?.taskCostings?.[0];
  const expectedValue = calculateExpectedTotalValueWithLogic(task);
  const actualValue = calculateActualTotalValue(task, completionAccomplishment, { enableDebug: false });
  const variance = calculateValueVariance(expectedValue, actualValue);
  const variancePercentage = calculateValueVariancePercentage(expectedValue, actualValue);

  const breakdown: any = {
    taskType: taskCosting?.taskType || 'Unknown',
    costType: taskCosting?.costType || 'Unknown',
    baseRate: taskCosting?.baseRate || 0,
    rateMultiplier: taskCosting?.rateMultiplier || 1,
    rateAdjustment: taskCosting?.rateAdjustment || 0,
    effectiveRate: (taskCosting?.rateAdjustment || 0) > 0 ? (taskCosting?.rateAdjustment || 0) : (taskCosting?.baseRate || 0),
    completionAccomplishment,
    expectedValue,
    actualValue,
    variance,
    variancePercentage
  };

  // Add effort breakdown for Cost Plus tasks
  if (taskCosting?.taskType === 'Lump Sum' && taskCosting?.costType === 'Cost Plus' && task?.efforts) {
    const labourEfforts = task.efforts.filter((effort: any) => isEffortType(effort, 'labour'));
    const materialEfforts = task.efforts.filter((effort: any) => isEffortType(effort, 'material'));
    const subcontractorEfforts = task.efforts.filter((effort: any) => isEffortType(effort, 'subcontractor'));
    const vehicleEfforts = task.efforts.filter((effort: any) => isEffortType(effort, 'vehicle'));
    const hiredEquipmentEfforts = task.efforts.filter((effort: any) =>
      isEffortType(effort, 'Hired Equipment', 'hired equipment', 'hiredequipment')
    );
    const otherEfforts = task.efforts.filter((effort: any) => isEffortType(effort, 'other'));

    const calculateEffortBreakdown = (efforts: any[], markup: number, labour = false) => {
      const value = efforts.reduce((sum: number, effort: any) => {
        return sum + (labour ? calculateLabourLineTotal(effort) : (effort.rate || 0) * (effort.quantity || 0));
      }, 0);
      const total = value * (1 + markup / 100);
      return { count: efforts.length, value, markup, total };
    };

    const calculateOtherEffortBreakdown = (efforts: any[]) => {
      const value = efforts.reduce((sum: number, effort: any) => {
        return sum + ((effort.rate || 0) * (effort.quantity || 0));
      }, 0);
      return { count: efforts.length, value, markup: 0, total: value }; // No markup for other costs
    };

    breakdown.effortBreakdown = {
      labour: calculateEffortBreakdown(labourEfforts, taskCosting.labourMarkup || 0, true),
      material: calculateEffortBreakdown(materialEfforts, taskCosting.materialMarkup || 0),
      subcontractor: calculateEffortBreakdown(subcontractorEfforts, taskCosting.subcontractorMarkup || 0),
      vehicle: calculateEffortBreakdown(vehicleEfforts, taskCosting.vehicleMarkup || 0),
      hiredEquipment: calculateEffortBreakdown(hiredEquipmentEfforts, taskCosting.hiredEquipmentMarkup || 0),
      other: calculateOtherEffortBreakdown(otherEfforts)
    };
  }

  return breakdown;
}; 
